import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { cn } from "~/lib/utils";
import { ShieldAlert, ShieldCheck, ShieldX } from "lucide-react";

interface TableHealth {
  name: string;
  rls_enabled: boolean;
  status: "secure" | "partial" | "unprotected";
  coverage: Record<string, "covered" | "missing">;
}

interface HealthSummary {
  total: number;
  secure: number;
  partial: number;
  unprotected: number;
}

export function RlsHealthBadge({ schema }: { schema: string }) {
  const [summary, setSummary] = useState<HealthSummary | null>(null);
  const [tables, setTables] = useState<TableHealth[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    fetch("/api/database/rls-health")
      .then((r) => r.json())
      .then((d) => {
        setSummary(d.summary);
        setTables(d.tables || []);
      })
      .catch(() => { toast.error("Failed to load RLS health"); })
      .finally(() => setLoading(false));
  }, [schema]);

  if (loading || !summary) return null;

  const color = summary.unprotected > 0
    ? "text-red-500 bg-red-500/10 border-red-500/20 hover:bg-red-500/20"
    : summary.partial > 0
      ? "text-amber-500 bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20"
      : "text-green-500 bg-green-500/10 border-green-500/20 hover:bg-green-500/20";

  const Icon = summary.unprotected > 0 ? ShieldX : summary.partial > 0 ? ShieldAlert : ShieldCheck;

  return (
    <>
      <button onClick={() => setOpen(true)}>
        <Badge className={cn("text-[11px] h-6 shrink-0 cursor-pointer", color)}>
          <Icon className="h-3 w-3 mr-1" />
          RLS: {summary.secure}/{summary.total}
        </Badge>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>RLS Policy Health</DialogTitle>
          </DialogHeader>

          <div className="flex gap-3 mb-4">
            <div className="flex items-center gap-1.5 text-xs">
              <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
              Secure ({summary.secure})
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />
              Partial ({summary.partial})
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
              Unprotected ({summary.unprotected})
            </div>
          </div>

          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-[13px] border-collapse">
              <thead className="sticky top-0 bg-background">
                <tr>
                  <th className="text-left py-2 px-2 border-b font-medium text-muted-foreground">Table</th>
                  <th className="text-center py-2 px-2 border-b font-medium text-muted-foreground w-16">RLS</th>
                  <th className="text-center py-2 px-2 border-b font-medium text-muted-foreground w-20">SELECT</th>
                  <th className="text-center py-2 px-2 border-b font-medium text-muted-foreground w-20">INSERT</th>
                  <th className="text-center py-2 px-2 border-b font-medium text-muted-foreground w-20">UPDATE</th>
                  <th className="text-center py-2 px-2 border-b font-medium text-muted-foreground w-20">DELETE</th>
                </tr>
              </thead>
              <tbody>
                {tables.map((t) => (
                  <tr key={t.name} className="hover:bg-muted/30">
                    <td className="py-1.5 px-2 border-b font-mono text-xs">{t.name}</td>
                    <td className="py-1.5 px-2 border-b text-center">
                      {t.rls_enabled ? (
                        <span className="text-green-500 text-xs">ON</span>
                      ) : (
                        <span className="text-red-500 text-xs">OFF</span>
                      )}
                    </td>
                    {["SELECT", "INSERT", "UPDATE", "DELETE"].map((op) => (
                      <td key={op} className="py-1.5 px-2 border-b text-center">
                        {!t.rls_enabled ? (
                          <span className="text-muted-foreground/30">-</span>
                        ) : t.coverage[op] === "covered" ? (
                          <div className="h-2 w-2 rounded-full bg-green-500 mx-auto" />
                        ) : (
                          <div className="h-2 w-2 rounded-full bg-red-500 mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
